import { useEffect } from "react";

const SITE_URL = "https://lavetesbaskets.com";
const DATA_ATTR = "data-seo-dynamic";

interface SeoProps {
  /** Page title (full — shown in the tab and in Google). */
  title: string;
  /** Meta description (~150-160 chars). */
  description: string;
  /** Path starting with "/", e.g. "/fr/tarifs". Used for canonical + og:url. */
  canonicalPath: string;
  /** Absolute or root-relative OG image. Defaults to the hero. */
  ogImage?: string;
  /** JSON-LD objects injected as <script type="application/ld+json">. */
  jsonLd?: object[];
  /** Keep private flows (auth, 404) out of the index. */
  noindex?: boolean;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Lightweight per-page head manager (no dependency): sets title, description,
 * canonical, Open Graph/Twitter tags and injects JSON-LD structured data.
 * Injected JSON-LD scripts are tagged and removed on unmount so navigating
 * between pages never accumulates stale schema.
 */
const Seo: React.FC<SeoProps> = ({
  title,
  description,
  canonicalPath,
  ogImage = `${SITE_URL}/og-cover.jpg`,
  jsonLd = [],
  noindex = false,
}) => {
  useEffect(() => {
    const url = `${SITE_URL}${canonicalPath}`;
    const image = ogImage.startsWith("http")
      ? ogImage
      : `${SITE_URL}${ogImage}`;

    document.title = title;
    document.documentElement.lang = canonicalPath.startsWith("/en") ? "en" : "fr";
    upsertMeta("name", "title", title);
    upsertMeta("name", "description", description);
    upsertCanonical(url);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "twitter:title", title);
    upsertMeta("property", "twitter:description", description);
    upsertMeta("property", "twitter:image", image);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Inject page-specific JSON-LD. A pre-rendered page already carries the
    // snapshot's copies: drop them first so hydration never duplicates them.
    document.head
      .querySelectorAll(`script[${DATA_ATTR}]`)
      .forEach((s) => s.remove());
    const scripts = jsonLd.map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(DATA_ATTR, "true");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      scripts.forEach((s) => s.remove());
    };
    // Serialize jsonLd for a stable dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalPath, ogImage, noindex, JSON.stringify(jsonLd)]);

  return null;
};

export { SITE_URL };
export default Seo;
